/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState} from 'react';
import {Alert, Platform} from 'react-native';

import {START_DATE} from '/enum/reminders.enum';
import {getSfmcUserDataFromStorage} from '/redux/action/sfmc.action';
import {getDateUtilDefaultInstance} from '/util/date.util';

import {
  convertISOtoDateTimeAMPM,
  currentDate,
  formatDate,
  formatDateToISOWithOffset,
  formatTime,
  getDateWithoutTime,
  isToday,
  ReminderData,
  validateEndDateForFrequency,
  validateTime,
} from '../ReminderUtils/ReminderUtils.util';

interface UseReminderParams {
  onDataReceived: (data: ReminderData) => void;
  onDelete: () => void;
  saveCheckInReminder: (reminder: ReminderData) => void;
  updateCheckInReminder: (reminder: ReminderData) => void;
  deleteReminder: (reminderId: string) => void;
  candidateId: string;
  candidateName: string;
  initialData?: ReminderData;
  isEdit?: boolean;
  coachEmail: string;
}

const useReminder = ({
  onDataReceived,
  initialData,
  isEdit,
  onDelete,
  saveCheckInReminder,
  deleteReminder,
  coachEmail,
  candidateId,
  candidateName,
  updateCheckInReminder,
}: UseReminderParams) => {
  const dateWithoutTime = getDateWithoutTime(currentDate());

  const [startDate, setStartDate] = useState<string>(
    initialData && initialData.formattedStartDate && isEdit
      ? initialData?.formattedStartDate
      : '',
  );
  const [time, setTime] = useState<string>(
    initialData && initialData.formattedTime && isEdit
      ? initialData.formattedTime
      : '',
  );
  const [repeat, setRepeat] = useState<string>(
    initialData && isEdit ? initialData.frequency : 'Does Not Repeat',
  );
  const [endDate, setEndDate] = useState<string>(
    initialData && isEdit ? initialData?.formattedEndDate || '' : '',
  );
  const [activeDateType, setActiveDateType] = useState<'start' | 'end' | null>(
    null,
  );
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState({title: '', message: ''});
  const [datePickerValue, setDatePickerValue] = useState<Date>(
    initialData && initialData.startDate && isEdit
      ? getDateWithoutTime(new Date(initialData?.startDate))
      : dateWithoutTime,
  );
  const [endDatePickerValue, setEndDatePickerValue] = useState<Date>(
    initialData && initialData.endDate && isEdit
      ? getDateWithoutTime(new Date(initialData.endDate))
      : dateWithoutTime,
  );
  const [timePickerValue, setTimePickerValue] = useState<Date>(
    initialData && initialData.time && isEdit
      ? new Date(initialData.time)
      : currentDate(),
  );
  const [loading, setLoading] = useState(false);

  // Effect to set initial values and check for changes
  useEffect(() => {
    if (initialData && isEdit) {
      setStartDate(initialData.formattedStartDate || '');
      setEndDate(initialData?.formattedEndDate || '');
      setTime(initialData.formattedTime || '');
      setRepeat(initialData.frequency);
      setDatePickerValue(
        initialData?.startDate
          ? getDateWithoutTime(new Date(initialData.startDate))
          : dateWithoutTime,
      );
      setTimePickerValue(
        initialData?.time ? new Date(initialData.time) : currentDate(),
      );
      setEndDatePickerValue(
        initialData?.endDate
          ? getDateWithoutTime(new Date(initialData.endDate))
          : dateWithoutTime,
      );
    }
  }, [
    isEdit,
    initialData,
    setStartDate,
    setTime,
    setRepeat,
    setDatePickerValue,
    setTimePickerValue,
    setEndDatePickerValue,
    setEndDate,
  ]);

  const getCrmContactIdByUsername = async (
    username: string,
  ): Promise<string | null> => {
    try {
      const data = await getSfmcUserDataFromStorage();
      if (data) {
        const userData = typeof data === 'string' ? JSON.parse(data) : data;
        if (Array.isArray(userData)) {
          const userEntry = userData.find(entry => entry[username]);
          return userEntry ? userEntry[username] : null;
        } else {
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const formatReminderData = (contactKey: string): ReminderData => {
    const userTimezone = getDateUtilDefaultInstance().getUserTimezone();
    const formattedStart = convertISOtoDateTimeAMPM(
      new Date(datePickerValue),
      new Date(timePickerValue),
    );

    let formattedEnd: any = null;
    if (endDate) {
      formattedEnd = convertISOtoDateTimeAMPM(
        new Date(endDatePickerValue),
        new Date(timePickerValue),
      );
    }

    const combinedStartDate = formattedStart?.combinedDate;
    const combinedEndDate = formattedEnd?.combinedDate;

    // If combinedDate is invalid, set to a default value
    const startDateFormatted = combinedStartDate
      ? formatDateToISOWithOffset(new Date(combinedStartDate))
      : null; // Handle invalid date

    const endDateFormatted = combinedEndDate
      ? formatDateToISOWithOffset(new Date(combinedEndDate))
      : null; // Handle invalid date

    const reminderData: any = {
      startDate: startDateFormatted,
      frequency: repeat,
      timezone: userTimezone,
      ...(!isEdit && {
        customerId: candidateId,
        name: candidateName,
      }),
    };

    if (!isEdit && contactKey) {
      reminderData.contactKey = contactKey;
    }
    // Only include `endDate` if it's available
    if (endDateFormatted) {
      reminderData.endDate = endDateFormatted;
    }

    return reminderData;
  };

  const showError = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      setErrorMessage({title, message});
      setErrorModalVisible(true);
    } else {
      Alert.alert(title, message, [{text: 'OK'}]);
    }
  };

  const handleSave = async () => {
    if (startDate && time && repeat) {
      // Validate time before proceeding
      if (!validateTime(datePickerValue, timePickerValue)) {
        setTime('');
        showError(
          'Invalid Time',
          'You cannot select a time in the past. Please choose a time that is in the future.',
        );
        return; // Exit the function early if validation fails
      }
      const dataToSave: ReminderData = {
        startDate: new Date(datePickerValue),
        time: new Date(timePickerValue),
        frequency: repeat,
      };
      if (endDate) {
        dataToSave.endDate = endDatePickerValue
          ? new Date(endDatePickerValue)
          : null;
      }
      try {
        setLoading(true);
        // Retrieve CRM contact ID first
        const retrievedCrmContactId = await getCrmContactIdByUsername(
          coachEmail,
        );
        if (!retrievedCrmContactId && Platform.OS !== 'web') return;
        const dataToBeSaved: ReminderData = formatReminderData(
          retrievedCrmContactId || '',
        );
        // Save the check-in reminder
        if (isEdit) {
          await updateCheckInReminder({
            reminderData: dataToBeSaved,
            candidateId,
          });
        } else {
          await saveCheckInReminder({
            reminderData: dataToBeSaved,
            candidateId,
          });
        }
        onDataReceived(dataToSave);
      } catch (error) {
        console.error(`There was an issue saving the reminder: ${error}`);
      } finally {
        setLoading(false);
      }
    } else {
      console.error('There was an issue saving the reminder');
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await deleteReminder(candidateId);
      onDelete();
    } catch (error) {
      console.error('Error deleting:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDatePicker = (isDate: boolean) => {
    setShowDatePicker(isDate);
    setShowTimePicker(!isDate);
    setModalVisible(false);
  };

  const isEndDateValid = (stDate: string, edDate: string) => {
    const dateUtil = getDateUtilDefaultInstance();
    const formatStr = 'EEE, MMM d, yyyy';
    const parsedStartDate = dateUtil.parseDate(stDate, formatStr);
    const parsedEndDate = dateUtil.parseDate(edDate, formatStr);

    if (!parsedStartDate || !parsedEndDate) {
      throw new Error('Invalid date format or parsing failed');
    }

    if (parsedEndDate <= parsedStartDate) {
      return false;
    }
    return true;
  };

  const onDateChange = (
    event: any,
    selectedDate?: Date,
    type?: 'start' | 'end',
  ) => {
    const currentType = type || activeDateType;
    if (event.type !== 'set' || !selectedDate) {
      setShowDatePicker(false); // Close the date picker on cancel or invalid selection
      return;
    }
    if (Platform.OS === 'android' || Platform.OS === 'web') {
      setShowDatePicker(false);
    }

    const formattedDate = formatDate(selectedDate);
    if (currentType === START_DATE) {
      setDatePickerValue(selectedDate);
      setStartDate(formattedDate);
      !isEdit && setTime(''); // Reset time when start date changes
      setEndDate('');
    } else {
      const validation = validateEndDateForFrequency(
        datePickerValue,
        selectedDate,
        repeat,
      );
      if (validation.isValid) {
        setEndDatePickerValue(selectedDate);
        setEndDate(formattedDate);
      } else {
        setEndDate('');
        showError(
          'Invalid End Date',
          `For ${repeat} reminders, the End Date must be on or after ${formatDate(
            validation.minDate,
          )}.`,
        );
      }
    }
  };

  const onTimeChange = (event: any, selectedTime?: Date) => {
    const selectedDate = datePickerValue;
    if (event.type === 'set' && selectedTime) {
      if (selectedDate) {
        if (!validateTime(selectedDate, selectedTime)) {
          if (Platform.OS === 'android') {
            setShowTimePicker(false);
            showError(
              'Invalid Time',
              'You cannot select a time in the past. Please choose a time that is in the future.',
            );
          }
        } else {
          if (Platform.OS === 'android') {
            setShowTimePicker(false);
          }
          setTimePickerValue(selectedTime);
          if (Platform.OS === 'ios') {
          } else {
            const formattedTime = formatTime(selectedTime);
            setTime(formattedTime);
          }
        }
      }
    } else {
      setShowTimePicker(false);
    }
  };

  const setDateText = () => {
    if (datePickerValue || endDatePickerValue) {
      setShowDatePicker(false);

      const formattedStartDate = formatDate(datePickerValue);
      const formattedEndDate = endDatePickerValue
        ? formatDate(endDatePickerValue)
        : '';

      if (activeDateType === START_DATE) {
        setStartDate(formattedStartDate); // Update the formatted start date
        !isEdit && setTime('');
      } else {
        const frequencyValidation = validateEndDateForFrequency(
          datePickerValue,
          endDatePickerValue,
          repeat,
        );
        const endDateValidation = isEndDateValid(
          formattedStartDate,
          formattedEndDate,
        );
        // Check if both validations pass
        if (frequencyValidation.isValid && endDateValidation) {
          setEndDate(formattedEndDate);
        } else {
          setEndDate('');
        }
      }
    }
  };

  const setTimeText = () => {
    if (timePickerValue) {
      if (!validateTime(datePickerValue, timePickerValue)) {
        if (Platform.OS === 'ios') {
          setShowTimePicker(false);
          showError(
            'Invalid Time',
            'You cannot select a time in the past. Please choose a time that is in the future.',
          );
        }
      } else {
        setShowTimePicker(false);
        const formattedTime = formatTime(timePickerValue);
        setTime(formattedTime);
      }
    }
  };

  const handleDateDone = () => {
    setDateText();
  };

  const handleTimeDone = () => {
    setTimeText();
  };

  return {
    startDate,
    time,
    repeat,
    showDatePicker,
    showTimePicker,
    modalVisible,
    errorModalVisible,
    errorMessage,
    datePickerValue,
    timePickerValue,
    setRepeat,
    setShowDatePicker,
    setShowTimePicker,
    setModalVisible,
    setErrorModalVisible,
    handleSave,
    toggleDatePicker,
    onDateChange,
    onTimeChange,
    handleDateDone,
    handleTimeDone,
    dateWithoutTime,
    currentDate,
    isToday,
    activeDateType,
    setActiveDateType,
    endDatePickerValue,
    endDate,
    isEndDateValid,
    setTimePickerValue,
    setTimeText,
    handleDelete,
    loading,
    setEndDatePickerValue,
    setEndDate,
    showError,
  };
};

export default useReminder;
